/**
 * ELITE BATCH 23 PART 8 — Workflows 119-135 (Support & Customer Success continued)
 * 17 support workflows: NPS recovery, outage comms, delivery rescue, returns, escalation detection.
 * Services: PagerDuty, StatusPage, ShipStation, Shopify, Calendly, Jira, HubSpot, OpenAI, Slack, Google Sheets.
 * Each workflow: 8 nodes, proper edges, descriptive names, y ~240 spacing.
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 119-135 ============

export const ELITE_BATCH23_PART8: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ────────────────────────────────────────────────────────────────
  // 119. NPS Detractor Recovery | Support | HubSpot
  // ────────────────────────────────────────────────────────────────
  nps_detractor_recovery: {
    nodes: [
      trigger("Webhook: NPS detractor score received (0-6)", "webhook_app", {
        source: "hubspot",
        event: "survey.response",
        howItStarts:
          "Fires when a customer submits an NPS score between 0 and 6 (detractor), delivering the score, free-text feedback, customer email, and contact ID.",
        setup:
          "Connect your NPS survey platform webhook (e.g., HubSpot feedback, Delighted, or Outgrow Forms). Filter for scores 0-6 only.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch customer account details and value",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,plan_tier,annual_revenue,lifecyclestage,hs_object_id",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company_name",
                type: "string",
              },
              {
                name: "properties.plan_tier",
                customName: "plan_tier",
                type: "string",
              },
              {
                name: "properties.annual_revenue",
                customName: "annual_revenue",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze detractor feedback and recommend recovery",
        520,
        "You are a customer success recovery specialist. Analyze the NPS detractor feedback to identify the root cause of dissatisfaction. Categorize into: product_issue, support_experience, pricing_concern, missing_feature, onboarding_friction, reliability. Recommend a specific recovery strategy. Return root_cause, category, severity, recovery_strategy, talking_points.",
        "Analyze NPS detractor feedback. Score: {nps_score}. Feedback: {nps_feedback}. Customer: {first_name} at {company_name}. Plan: {plan_tier}. Revenue: {annual_revenue}.",
        [
          { field: "root_cause", as: "root_cause", type: "string" },
          { field: "category", as: "detractor_category", type: "string" },
          { field: "severity", as: "severity_level", type: "string" },
          {
            field: "recovery_strategy",
            as: "recovery_strategy",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
        ],
      ),
      n("4", "runCode", "Select recovery action based on customer value", 760, {
        runCodeDetails: {
          code: "const revenue = parseFloat(annual_revenue) || 0; const high_value = revenue > 50000 || plan_tier === 'enterprise'; const recovery_urgency = nps_score <= 3 ? 'critical' : 'high'; const needs_exec_involvement = high_value && nps_score <= 3; return { high_value, recovery_urgency, needs_exec_involvement, recovery_owner: high_value ? 'CSM + Manager' : 'CSM' };",
          variables: [
            { name: "high_value", customName: "high_value", type: "boolean" },
            {
              name: "recovery_urgency",
              customName: "recovery_urgency",
              type: "string",
            },
            {
              name: "needs_exec_involvement",
              customName: "needs_exec_involvement",
              type: "boolean",
            },
            {
              name: "recovery_owner",
              customName: "recovery_owner",
              type: "string",
            },
          ],
        },
      }),
      n("5", "condition", "Check: Is this a high-value customer?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{high_value}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "6",
        "approvalGate",
        "CSM Review: Approve recovery outreach plan",
        1240,
        {
          approvalGateDetails: {
            approvers: ["{csm_email}"],
            message:
              "NPS Detractor Recovery — {first_name} at {company_name}\n\nScore: {nps_score}/10\nCategory: {detractor_category}\nRoot Cause: {root_cause}\nStrategy: {recovery_strategy}\n\nPlease review and approve the recovery outreach.",
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send personalized recovery outreach",
        1480,
        "{customer_email}",
        "We hear you, {first_name} — let's make this right",
        "Hi {first_name},\n\nThank you for sharing your honest feedback. I've personally reviewed your concerns and I want to make sure we address them.\n\n{recovery_strategy}\n\nI'd love to schedule a brief call to discuss how we can improve your experience. You can book a time here: {calendly_link}\n\nYour satisfaction is our top priority, and we're committed to earning back your trust.\n\nBest regards,\n{csm_name}\nCustomer Success Manager",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log NPS detractor recovery case",
        1720,
        "NPS_Recovery",
        '[["{customer_email}","{company_name}","{nps_score}","{detractor_category}","{root_cause}","{recovery_urgency}","{recovery_owner}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers on NPS detractor scores (0-6), fetches the customer profile from HubSpot, uses AI to analyze the root cause and recommend a recovery strategy, routes high-value accounts through CSM approval, then sends a personalized recovery email. All detractor cases are logged to Sheets for trend analysis.",
  },

  // ────────────────────────────────────────────────────────────────
  // 120. NPS Passive Nurture | Support | HubSpot
  // ────────────────────────────────────────────────────────────────
  nps_passive_nurture: {
    nodes: [
      trigger("Webhook: NPS passive score received (7-8)", "webhook_app", {
        source: "hubspot",
        event: "survey.response",
        howItStarts:
          "Fires when a customer submits an NPS score of 7 or 8 (passive), delivering the score, feedback, customer email, and contact ID.",
        setup:
          "Connect your NPS survey webhook and filter for scores 7-8 (passives). These customers are at risk of churn but not actively unhappy.",
        inputFields: [],
      }),
      n("2", "hitApi", "HubSpot: Fetch customer profile and usage data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,company,plan_tier,last_active_date,feature_adoption_score,hs_object_id",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "properties.company",
              customName: "company_name",
              type: "string",
            },
            {
              name: "properties.plan_tier",
              customName: "plan_tier",
              type: "string",
            },
            {
              name: "properties.feature_adoption_score",
              customName: "adoption_score",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "OpenAI: Identify education opportunities for passive",
        520,
        "You are a customer success strategist. Analyze this NPS passive customer profile to identify features or capabilities they may not be using. Suggest specific educational content that could increase their satisfaction from passive to promoter. Return unused_features, education_topic, content_recommendation, upgrade_opportunity.",
        "Analyze NPS passive customer for education opportunities. Score: {nps_score}. Feedback: {nps_feedback}. Plan: {plan_tier}. Adoption score: {adoption_score}/100. Company: {company_name}.",
        [
          { field: "unused_features", as: "unused_features", type: "string" },
          { field: "education_topic", as: "education_topic", type: "string" },
          {
            field: "content_recommendation",
            as: "content_recommendation",
            type: "string",
          },
          {
            field: "upgrade_opportunity",
            as: "upgrade_opportunity",
            type: "string",
          },
        ],
      ),
      n("4", "runCode", "Select nurture content based on adoption gaps", 760, {
        runCodeDetails: {
          code: "const low_adoption = adoption_score < 50; const content_type = low_adoption ? 'getting_started_advanced' : 'power_user_tips'; const nurture_track = plan_tier === 'enterprise' ? 'enterprise_value' : plan_tier === 'professional' ? 'pro_optimization' : 'starter_growth'; return { content_type, nurture_track, low_adoption };",
          variables: [
            {
              name: "content_type",
              customName: "content_type",
              type: "string",
            },
            {
              name: "nurture_track",
              customName: "nurture_track",
              type: "string",
            },
            {
              name: "low_adoption",
              customName: "low_adoption",
              type: "boolean",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send educational content to passive customer",
        1000,
        "{customer_email}",
        "{first_name}, unlock more value from your {plan_tier} plan",
        "Hi {first_name},\n\nThank you for your recent feedback. We noticed you might benefit from exploring some features that could make a big difference for {company_name}.\n\n{content_recommendation}\n\nHere are some resources to help you get more value:\n- {education_topic}\n- Unused features you should try: {unused_features}\n\nWe're here to help you succeed. Reply to this email if you'd like a personalized walkthrough.\n\nBest,\nCustomer Success Team",
      ),
      n("6", "delayAction", "Wait 14 days for engagement measurement", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Track passive nurture and improvement",
        1480,
        "NPS_Passive_Nurture",
        '[["{customer_email}","{company_name}","{nps_score}","{adoption_score}","{content_type}","{nurture_track}","{education_topic}","{today_date}"]]',
      ),
      n("8", "runCode", "Schedule follow-up NPS survey in 90 days", 1720, {
        runCodeDetails: {
          code: "const follow_up_date = new Date(Date.now() + 90 * 86400000).toISOString().split('T')[0]; const nurture_complete = true; return { follow_up_survey_date: follow_up_date, nurture_complete, days_until_resurvey: 90 };",
          variables: [
            {
              name: "follow_up_survey_date",
              customName: "follow_up_survey_date",
              type: "string",
            },
            {
              name: "nurture_complete",
              customName: "nurture_complete",
              type: "boolean",
            },
            {
              name: "days_until_resurvey",
              customName: "days_until_resurvey",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers on NPS passive scores (7-8), fetches the customer profile and usage data from HubSpot, uses AI to identify feature adoption gaps and recommend education content. Sends a personalized educational email, waits 14 days to measure engagement, logs the nurture activity, and schedules a 90-day follow-up survey.",
  },

  // ────────────────────────────────────────────────────────────────
  // 121. Outage Inquiry Bot | Support | StatusPage
  // ────────────────────────────────────────────────────────────────
  outage_inquiry_bot: {
    nodes: [
      trigger(
        "Webhook: Customer asks about service status during incident",
        "webhook_app",
        {
          source: "intercom",
          event: "conversation.user.created",
          howItStarts:
            "Fires when a customer sends a message during an active incident, delivering the message text, conversation ID, and user details for contextual status response.",
          setup:
            "Connect Intercom webhook for new conversations. This workflow automatically checks StatusPage for active incidents before responding.",
          inputFields: [],
        },
      ),
      n("2", "hitApi", "StatusPage: Check current incident status", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.statuspage.io/v1/pages/{page_id}/incidents/unresolved.json",
          headers: [
            { key: "Authorization", value: "OAuth {statuspage_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "[0].name", customName: "incident_name", type: "string" },
            {
              name: "[0].status",
              customName: "incident_status",
              type: "string",
            },
            {
              name: "[0].impact",
              customName: "incident_impact",
              type: "string",
            },
            {
              name: "[0].updated_at",
              customName: "last_update_time",
              type: "string",
            },
            {
              name: "[0].incident_updates[0].body",
              customName: "latest_update_body",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "OpenAI: Generate contextual incident response",
        520,
        "You are a support bot during a service incident. Using the current StatusPage incident data, generate a clear, empathetic response addressing the customer concern. Include: what is affected, current status, expected resolution, and link to status page. Return contextual_response, is_related_to_incident, affected_services.",
        "Customer message: {message_text}. Active incident: {incident_name}. Status: {incident_status}. Impact: {incident_impact}. Latest update: {latest_update_body}. Last updated: {last_update_time}.",
        [
          {
            field: "contextual_response",
            as: "contextual_response",
            type: "string",
          },
          {
            field: "is_related_to_incident",
            as: "is_related_to_incident",
            type: "string",
          },
          {
            field: "affected_services",
            as: "affected_services",
            type: "string",
          },
        ],
      ),
      n("4", "runCode", "Check if customer is affected by the incident", 760, {
        runCodeDetails: {
          code: "const customer_affected = is_related_to_incident === 'true'; const status_page_url = 'https://status.{company_domain}.com'; const severity_label = incident_impact === 'critical' ? 'Major Outage' : incident_impact === 'major' ? 'Partial Outage' : 'Minor Issue'; return { customer_affected, status_page_url, severity_label };",
          variables: [
            {
              name: "customer_affected",
              customName: "customer_affected",
              type: "boolean",
            },
            {
              name: "status_page_url",
              customName: "status_page_url",
              type: "string",
            },
            {
              name: "severity_label",
              customName: "severity_label",
              type: "string",
            },
          ],
        },
      }),
      n(
        "5",
        "condition",
        "Check: Is customer affected by active incident?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{customer_affected}",
                operator: "==",
                value: "true",
              },
            ],
          },
        },
      ),
      n("6", "sendMessage", "Send incident status update to customer", 1240, {
        sendMessageDetails: {
          channel: "intercom",
          conversationId: "{conversation_id}",
          message:
            "{contextual_response}\n\nFor real-time updates, visit our status page: {status_page_url}",
        },
      }),
      slackNode(
        "7",
        "Slack: Log customer inquiry during incident",
        1480,
        ":rotating_light: *Incident Inquiry Logged*\n*Incident:* {incident_name} ({severity_label})\n*Customer Message:* {message_text}\n*Related to Incident:* {is_related_to_incident}\n*Affected Services:* {affected_services}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log incident inquiry volume",
        1720,
        "Incident_Inquiries",
        '[["{conversation_id}","{incident_name}","{incident_status}","{customer_affected}","{severity_label}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a customer sends a message during an active incident, checks StatusPage for current incident status, uses AI to generate a contextual response explaining the situation, determines if the customer is affected, sends an automated status update with a link to the status page, and logs inquiry volume to Sheets for incident communication analysis.",
  },

  // ────────────────────────────────────────────────────────────────
  // 122. Outage Notification Workflow | Support | PagerDuty
  // ────────────────────────────────────────────────────────────────
  outage_notification_workflow: {
    nodes: [
      trigger("PagerDuty webhook: Incident triggered", "webhook_app", {
        source: "pagerduty",
        event: "incident.triggered",
        howItStarts:
          "Fires when PagerDuty triggers a new incident, delivering the incident ID, title, severity, affected service, and trigger details.",
        setup:
          "Connect PagerDuty webhook for incident.triggered events. This workflow proactively notifies affected customers before they contact support.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Draft customer-facing notification",
        280,
        "You are an incident communications specialist. Draft a clear, professional customer notification about the incident. Include: what is affected, current impact, what we are doing, expected timeline if known. Avoid technical jargon. Create versions for email and SMS. Return email_subject, email_body, sms_message, affected_component, estimated_resolution.",
        "Draft customer notification for incident. Title: {incident_title}. Severity: {incident_severity}. Service: {affected_service}. Description: {incident_description}.",
        [
          {
            field: "email_subject",
            as: "notification_subject",
            type: "string",
          },
          { field: "email_body", as: "notification_body", type: "string" },
          { field: "sms_message", as: "sms_notification", type: "string" },
          {
            field: "affected_component",
            as: "affected_component",
            type: "string",
          },
          {
            field: "estimated_resolution",
            as: "estimated_resolution",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Segment affected customers by service and severity",
        520,
        {
          runCodeDetails: {
            code: "const severity_map = { critical: 'P1', high: 'P2', warning: 'P3', info: 'P4' }; const priority = severity_map[incident_severity] || 'P3'; const is_major = priority === 'P1' || priority === 'P2'; const notification_channels = is_major ? ['email', 'sms'] : ['email']; return { priority, is_major, notification_channels: notification_channels.join(','), customer_segment: affected_service + '_users' };",
            variables: [
              {
                name: "priority",
                customName: "incident_priority",
                type: "string",
              },
              {
                name: "is_major",
                customName: "is_major_incident",
                type: "boolean",
              },
              {
                name: "notification_channels",
                customName: "notification_channels",
                type: "string",
              },
              {
                name: "customer_segment",
                customName: "customer_segment",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Is severity major or higher?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_major_incident}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send proactive outage notification to customers",
        1000,
        "{affected_customers_email_list}",
        "{notification_subject}",
        "{notification_body}\n\nEstimated Resolution: {estimated_resolution}\n\nFor real-time updates, visit our status page: {status_page_url}\n\nWe apologize for the inconvenience and are working to resolve this as quickly as possible.",
      ),
      smsNode(
        "6",
        "Twilio: Send urgent SMS alert for major incidents",
        1240,
        "{affected_customers_phone_list}",
        "{sms_notification} Status: {status_page_url}",
      ),
      slackNode(
        "7",
        "Slack: Post internal incident update",
        1480,
        ":fire: *Incident Notification Sent — {incident_priority}*\n*Incident:* {incident_title}\n*Severity:* {incident_severity}\n*Affected:* {affected_component}\n*Channels:* {notification_channels}\n*Segment:* {customer_segment}\n*ETA:* {estimated_resolution}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log incident notification dispatch",
        1720,
        "Incident_Notifications",
        '[["{incident_id}","{incident_title}","{incident_priority}","{affected_component}","{notification_channels}","{estimated_resolution}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers on PagerDuty incidents, uses AI to draft customer-facing notifications, segments affected customers by service, and dispatches proactive email notifications. For major incidents (P1/P2), also sends urgent SMS alerts via Twilio. Posts an internal Slack update and logs all notification dispatches to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 123. Bug or Incident Customer Communication | Support | PagerDuty
  // ────────────────────────────────────────────────────────────────
  bug_incident_customer_communication: {
    nodes: [
      trigger(
        "PagerDuty webhook: Incident updated or resolved",
        "webhook_app",
        {
          source: "pagerduty",
          event: "incident.resolved",
          howItStarts:
            "Fires when a PagerDuty incident is updated or resolved, delivering the incident ID, title, status, resolution summary, and timeline.",
          setup:
            "Connect PagerDuty webhook for incident state changes (acknowledged, resolved). This workflow manages ongoing customer communication throughout the incident lifecycle.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract impact scope and draft communication",
        280,
        "You are an incident communications manager. Analyze the incident resolution details and draft a customer-facing post-incident communication. Include: what happened, impact scope, resolution steps taken, preventive measures planned. Return incident_summary, impact_description, resolution_details, prevention_plan, communication_draft.",
        "Draft post-incident communication. Incident: {incident_title}. Status: {incident_status}. Resolution: {resolution_summary}. Duration: {incident_duration}.",
        [
          { field: "incident_summary", as: "incident_summary", type: "string" },
          {
            field: "impact_description",
            as: "impact_description",
            type: "string",
          },
          {
            field: "resolution_details",
            as: "resolution_details",
            type: "string",
          },
          { field: "prevention_plan", as: "prevention_plan", type: "string" },
          {
            field: "communication_draft",
            as: "communication_draft",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Identify affected customers from incident data", 520, {
        runCodeDetails: {
          code: "const affected_count = affected_customer_ids ? affected_customer_ids.split(',').length : 0; const needs_postmortem = incident_duration_minutes > 60; const severity_label = incident_severity === 'critical' ? 'Major' : 'Minor'; return { affected_count, needs_postmortem, severity_label, communication_type: needs_postmortem ? 'post_mortem' : 'resolution_notice' };",
          variables: [
            {
              name: "affected_count",
              customName: "affected_count",
              type: "number",
            },
            {
              name: "needs_postmortem",
              customName: "needs_postmortem",
              type: "boolean",
            },
            {
              name: "severity_label",
              customName: "severity_label",
              type: "string",
            },
            {
              name: "communication_type",
              customName: "communication_type",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "OpenAI: Draft final multi-channel communication",
        760,
        "You are a crisis communications expert. Polish the incident communication for professional distribution. Create email and Slack-friendly versions. Keep tone empathetic and transparent. Return email_version, slack_version, subject_line.",
        "Polish this incident communication for customers. Summary: {incident_summary}. Impact: {impact_description}. Resolution: {resolution_details}. Prevention: {prevention_plan}. Type: {communication_type}.",
        [
          { field: "email_version", as: "email_communication", type: "string" },
          { field: "slack_version", as: "slack_communication", type: "string" },
          { field: "subject_line", as: "email_subject", type: "string" },
        ],
      ),
      n(
        "5",
        "approvalGate",
        "Incident Commander: Approve customer communication",
        1000,
        {
          approvalGateDetails: {
            approvers: ["{incident_commander_email}"],
            message:
              "Incident Communication Approval\n\nIncident: {incident_title}\nType: {communication_type}\nAffected Customers: {affected_count}\n\nDraft:\n{email_communication}\n\nPlease approve to dispatch to affected customers.",
          },
        },
      ),
      emailNode(
        "6",
        "Email: Dispatch multi-channel incident communication",
        1240,
        "{affected_customers_email_list}",
        "{email_subject}",
        "{email_communication}",
      ),
      slackNode(
        "7",
        "Slack: Post internal resolution update",
        1480,
        ":white_check_mark: *Incident Communication Dispatched*\n*Incident:* {incident_title} ({severity_label})\n*Type:* {communication_type}\n*Affected:* {affected_count} customers\n*Postmortem needed:* {needs_postmortem}\n\n{slack_communication}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log incident communication timeline",
        1720,
        "Incident_Comms_Timeline",
        '[["{incident_id}","{incident_title}","{severity_label}","{communication_type}","{affected_count}","{needs_postmortem}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Manages customer communication for PagerDuty incidents through resolution. AI drafts professional post-incident communications, identifies affected customers, and creates multi-channel versions. The incident commander approves before dispatch. Emails are sent to affected customers, the internal team gets a Slack update, and the full communication timeline is logged.",
  },

  // ────────────────────────────────────────────────────────────────
  // 124. Delivery Delay Apology + Support | Support | ShipStation + Shopify
  // ────────────────────────────────────────────────────────────────
  delivery_delay_apology_support: {
    nodes: [
      trigger("ShipStation webhook: Delivery delay detected", "webhook_app", {
        source: "shipstation",
        event: "SHIP_NOTIFY",
        howItStarts:
          "Fires when ShipStation detects a shipping delay or delivery exception, providing the order ID, tracking number, expected vs actual delivery date, and carrier details.",
        setup:
          "Connect ShipStation webhook for shipping notifications. Filter for delay events where estimated delivery exceeds original promise date.",
        inputFields: [],
      }),
      n("2", "hitApi", "Shopify: Fetch order details and customer info", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_domain}.myshopify.com/admin/api/2024-01/orders/{order_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "order.customer.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "order.customer.first_name",
              customName: "first_name",
              type: "string",
            },
            { name: "order.name", customName: "order_number", type: "string" },
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
            {
              name: "order.line_items[0].title",
              customName: "primary_item",
              type: "string",
            },
          ],
        },
      }),
      n(
        "3",
        "runCode",
        "Compute delay severity and determine compensation",
        520,
        {
          runCodeDetails: {
            code: "const expected = new Date(expected_delivery); const now = new Date(); const delay_days = Math.round((now - expected) / 86400000); const severe = delay_days > 5; const compensation = severe ? 'free_shipping_next_order' : delay_days > 3 ? '10_pct_discount' : 'apology_only'; const new_eta = new Date(now.getTime() + 3 * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); return { delay_days, severe, compensation, new_eta };",
            variables: [
              { name: "delay_days", customName: "delay_days", type: "number" },
              { name: "severe", customName: "severe_delay", type: "boolean" },
              {
                name: "compensation",
                customName: "compensation_type",
                type: "string",
              },
              { name: "new_eta", customName: "new_eta", type: "string" },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Is delay more than 5 days?", 760, {
        conditionDetails: {
          conditions: [{ variable: "{delay_days}", operator: ">", value: "5" }],
        },
      }),
      emailNode(
        "5",
        "Email: Send proactive delay apology to customer",
        1000,
        "{customer_email}",
        "Update on your order {order_number} — we're sorry for the delay",
        "Hi {first_name},\n\nWe wanted to let you know that your order {order_number} ({primary_item}) is experiencing a shipping delay of {delay_days} days.\n\nNew estimated delivery: {new_eta}\nTracking: {tracking_url}\n\nWe sincerely apologize for the inconvenience. As a token of our apology, we're offering you {compensation_type} on your next order.\n\nIf you have any questions, simply reply to this email.\n\nSorry again,\nCustomer Support Team",
      ),
      smsNode(
        "6",
        "Twilio: Send urgent SMS for severe delays",
        1240,
        "{customer_phone}",
        "Hi {first_name}, your order {order_number} is delayed {delay_days} days. New ETA: {new_eta}. We're sorry — check email for a make-good offer. Track: {tracking_url}",
      ),
      slackNode(
        "7",
        "Slack: Alert fulfillment team about delay",
        1480,
        ":package: *Delivery Delay Alert*\n*Order:* {order_number} | *Customer:* {first_name}\n*Item:* {primary_item} | *Value:* ${order_total}\n*Delay:* {delay_days} days | *Severe:* {severe_delay}\n*Compensation:* {compensation_type}\n*New ETA:* {new_eta}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log delivery delay and compensation",
        1720,
        "Delivery_Delays",
        '[["{order_number}","{customer_email}","{primary_item}","{delay_days}","{severe_delay}","{compensation_type}","{new_eta}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Detects ShipStation delivery delays, fetches the order from Shopify, computes delay severity and appropriate compensation. Proactively emails the customer an apology with a new ETA and make-good offer. For severe delays (5+ days), also sends an urgent SMS. Alerts the fulfillment team via Slack and logs everything to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 125. Failed Delivery Rescue | Support | ShipStation
  // ────────────────────────────────────────────────────────────────
  failed_delivery_rescue: {
    nodes: [
      trigger(
        "Webhook: Delivery failure notification received",
        "webhook_app",
        {
          source: "shipstation",
          event: "SHIP_NOTIFY",
          howItStarts:
            "Fires when a delivery attempt fails (returned to sender, address issue, refused, etc.), providing the order ID, tracking number, failure reason, and carrier details.",
          setup:
            "Connect ShipStation webhook for shipping notifications. Filter for delivery failure events (status: delivery_failed, returned_to_sender).",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract failure reason and determine rescue path",
        280,
        "You are a delivery rescue specialist. Analyze the delivery failure reason and determine the best rescue path: address_correction (customer needs to update address), redeliver (carrier retry), reship (send new package), refund (if undeliverable). Return failure_reason, failure_category, rescue_path, customer_action_needed, rescue_urgency.",
        "Analyze delivery failure. Tracking: {tracking_number}. Failure reason: {delivery_failure_reason}. Carrier: {carrier_name}. Attempts: {delivery_attempts}.",
        [
          { field: "failure_reason", as: "failure_reason", type: "string" },
          { field: "failure_category", as: "failure_category", type: "string" },
          { field: "rescue_path", as: "rescue_path", type: "string" },
          {
            field: "customer_action_needed",
            as: "customer_action_needed",
            type: "string",
          },
          { field: "rescue_urgency", as: "rescue_urgency", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "ShipStation: Fetch full order and shipping details",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://ssapi.shipstation.com/orders/{order_id}",
            headers: [
              { key: "Authorization", value: "Basic {shipstation_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "orderNumber",
                customName: "order_number",
                type: "string",
              },
              {
                name: "shipTo.name",
                customName: "customer_name",
                type: "string",
              },
              {
                name: "shipTo.street1",
                customName: "ship_address",
                type: "string",
              },
              {
                name: "customerEmail",
                customName: "customer_email",
                type: "string",
              },
              { name: "orderTotal", customName: "order_total", type: "number" },
            ],
          },
        },
      ),
      n("4", "runCode", "Determine rescue action and compose options", 760, {
        runCodeDetails: {
          code: "const is_address_issue = failure_category === 'address_correction'; const rescue_options = is_address_issue ? 'Please update your shipping address so we can resend your order.' : rescue_path === 'redeliver' ? 'We will attempt redelivery within 1-2 business days.' : 'We will ship a replacement to you right away.'; return { is_address_issue, rescue_options, needs_customer_input: is_address_issue };",
          variables: [
            {
              name: "is_address_issue",
              customName: "is_address_issue",
              type: "boolean",
            },
            {
              name: "rescue_options",
              customName: "rescue_options",
              type: "string",
            },
            {
              name: "needs_customer_input",
              customName: "needs_customer_input",
              type: "boolean",
            },
          ],
        },
      }),
      n(
        "5",
        "condition",
        "Check: Is this an address issue requiring customer input?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_address_issue}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "6",
        "Email: Send delivery rescue options to customer",
        1240,
        "{customer_email}",
        "Delivery issue with your order {order_number} — we need your help",
        "Hi {customer_name},\n\nUnfortunately, we were unable to deliver your order {order_number}.\n\nReason: {failure_reason}\n\n{rescue_options}\n\nCurrent address on file: {ship_address}\n\nPlease reply with your corrected address or click here to update: {address_update_link}\n\nWe'll get your order to you as soon as possible.\n\nSorry for the trouble,\nSupport Team",
      ),
      smsNode(
        "7",
        "Twilio: Send SMS rescue notification with link",
        1480,
        "{customer_phone}",
        "Hi {customer_name}, delivery of order {order_number} failed ({failure_reason}). {rescue_options} Update here: {address_update_link}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log delivery failure and rescue path",
        1720,
        "Delivery_Failures",
        '[["{order_number}","{customer_email}","{failure_category}","{rescue_path}","{is_address_issue}","{rescue_urgency}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Handles delivery failures by using AI to analyze the failure reason and determine the rescue path. Fetches order details from ShipStation, determines if customer input is needed (address correction) or if automatic reshipment can proceed. Emails and texts the customer with rescue options and logs all failures for carrier performance analysis.",
  },

  // ────────────────────────────────────────────────────────────────
  // 126. Return Initiated Confirmation | Support | Shopify
  // ────────────────────────────────────────────────────────────────
  return_initiated_confirmation: {
    nodes: [
      trigger("Shopify webhook: Return request submitted", "webhook_app", {
        source: "shopify",
        event: "returns/request",
        howItStarts:
          "Fires when a customer submits a return request in Shopify, delivering the return ID, order ID, items to return, return reason, and customer details.",
        setup:
          "Connect Shopify webhook for return request events. Ensure your return policy rules are configured in the workflow for eligibility validation.",
        inputFields: [],
      }),
      n("2", "hitApi", "Shopify: Fetch original order details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_domain}.myshopify.com/admin/api/2024-01/orders/{order_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "order.customer.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "order.customer.first_name",
              customName: "first_name",
              type: "string",
            },
            { name: "order.name", customName: "order_number", type: "string" },
            {
              name: "order.created_at",
              customName: "order_date",
              type: "string",
            },
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
            {
              name: "order.line_items",
              customName: "order_items",
              type: "object",
            },
          ],
        },
      }),
      n("3", "runCode", "Validate return eligibility per policy", 520, {
        runCodeDetails: {
          code: "const order_age_days = Math.round((Date.now() - new Date(order_date).getTime()) / 86400000); const within_window = order_age_days <= 30; const eligible = within_window; const return_label_url = eligible ? '{return_label_base_url}/{return_id}' : ''; const refund_estimate = eligible ? order_total : '0'; return { order_age_days, within_window, eligible, return_label_url, refund_estimate };",
          variables: [
            {
              name: "order_age_days",
              customName: "order_age_days",
              type: "number",
            },
            {
              name: "eligible",
              customName: "return_eligible",
              type: "boolean",
            },
            {
              name: "return_label_url",
              customName: "return_label_url",
              type: "string",
            },
            {
              name: "refund_estimate",
              customName: "refund_estimate",
              type: "string",
            },
          ],
        },
      }),
      n(
        "4",
        "condition",
        "Check: Is the return eligible per 30-day policy?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{return_eligible}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Send return confirmation with shipping label",
        1000,
        "{customer_email}",
        "Return approved for order {order_number} — shipping label enclosed",
        "Hi {first_name},\n\nYour return request for order {order_number} has been approved.\n\nReturn Reason: {return_reason}\nEstimated Refund: ${refund_estimate}\n\nHere is your prepaid return shipping label: {return_label_url}\n\nPlease ship the item(s) within 14 days. Your refund will be processed within 5-7 business days after we receive the return.\n\nIf you have questions, reply to this email.\n\nThank you,\nSupport Team",
      ),
      slackNode(
        "6",
        "Slack: Notify fulfillment of incoming return",
        1240,
        ":leftwards_arrow_with_hook: *Return Approved*\n*Order:* {order_number} | *Customer:* {first_name}\n*Reason:* {return_reason}\n*Refund Estimate:* ${refund_estimate}\n*Order Age:* {order_age_days} days\n*Eligible:* {return_eligible}",
      ),
      n("7", "hitApi", "Shopify: Update return status to approved", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_domain}.myshopify.com/admin/api/2024-01/returns/{return_id}/approve.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "return.id",
              customName: "approved_return_id",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log return request and eligibility",
        1720,
        "Return_Requests",
        '[["{order_number}","{customer_email}","{return_reason}","{return_eligible}","{refund_estimate}","{order_age_days}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes Shopify return requests by fetching the original order, validating eligibility against the 30-day return window, and sending the customer a confirmation email with a prepaid shipping label if eligible. Notifies the fulfillment team via Slack, updates the return status in Shopify, and logs all requests to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 127. Return Received + Refund Status | Support | Shopify
  // ────────────────────────────────────────────────────────────────
  return_received_refund_status: {
    nodes: [
      trigger("Webhook: Warehouse confirms return receipt", "webhook_app", {
        source: "shopify",
        event: "returns/update",
        howItStarts:
          "Fires when the warehouse scans a returned package and confirms receipt, delivering the return ID, order ID, items received, and condition assessment.",
        setup:
          "Connect Shopify webhook for return status updates. Filter for 'returned' or 'received' status changes indicating warehouse receipt.",
        inputFields: [],
      }),
      n("2", "hitApi", "Shopify: Fetch return and order details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_domain}.myshopify.com/admin/api/2024-01/orders/{order_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "order.customer.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "order.customer.first_name",
              customName: "first_name",
              type: "string",
            },
            { name: "order.name", customName: "order_number", type: "string" },
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
            {
              name: "order.financial_status",
              customName: "financial_status",
              type: "string",
            },
          ],
        },
      }),
      n("3", "runCode", "Process inspection and determine refund amount", 520, {
        runCodeDetails: {
          code: "const item_condition = inspection_result || 'good'; const full_refund = item_condition === 'good' || item_condition === 'unopened'; const refund_amount = full_refund ? parseFloat(order_total) : parseFloat(order_total) * 0.8; const refund_type = full_refund ? 'full' : 'partial_80_pct'; const approved = item_condition !== 'damaged_by_customer'; return { refund_amount: refund_amount.toFixed(2), refund_type, approved, item_condition };",
          variables: [
            {
              name: "refund_amount",
              customName: "refund_amount",
              type: "string",
            },
            { name: "refund_type", customName: "refund_type", type: "string" },
            {
              name: "approved",
              customName: "refund_approved",
              type: "boolean",
            },
            {
              name: "item_condition",
              customName: "item_condition",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Check: Is the refund approved?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{refund_approved}", operator: "==", value: "true" },
          ],
        },
      }),
      n("5", "hitApi", "Shopify: Initiate refund for approved return", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_domain}.myshopify.com/admin/api/2024-01/orders/{order_id}/refunds.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "refund",
              value:
                '{"notify":true,"note":"Return received. Condition: {item_condition}. Refund type: {refund_type}.","shipping":{"full_refund":true},"transactions":[{"kind":"refund","amount":"{refund_amount}"}]}',
            },
          ],
          variables: [
            { name: "refund.id", customName: "refund_id", type: "string" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Notify customer of refund processing",
        1240,
        "{customer_email}",
        "Your return is received — refund of ${refund_amount} is processing",
        "Hi {first_name},\n\nGreat news! We've received your return for order {order_number}.\n\nItem Condition: {item_condition}\nRefund Amount: ${refund_amount} ({refund_type})\n\nYour refund will appear on your original payment method within 5-7 business days.\n\nThank you for your patience,\nSupport Team",
      ),
      slackNode(
        "7",
        "Slack: Notify team of return receipt and refund",
        1480,
        ":white_check_mark: *Return Received & Refund Processed*\n*Order:* {order_number} | *Customer:* {first_name}\n*Condition:* {item_condition}\n*Refund:* ${refund_amount} ({refund_type})\n*Approved:* {refund_approved}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log return receipt and refund details",
        1720,
        "Return_Receipts",
        '[["{order_number}","{customer_email}","{item_condition}","{refund_amount}","{refund_type}","{refund_approved}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When the warehouse confirms receipt of a return, fetches the order from Shopify, inspects the item condition, and calculates the refund amount (full for good condition, 80% for used). If approved, initiates the Shopify refund, emails the customer with refund details, notifies the team via Slack, and logs to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 128. Exchange Request Workflow | Support | Shopify
  // ────────────────────────────────────────────────────────────────
  exchange_request_workflow: {
    nodes: [
      trigger("Webhook: Customer requests an exchange", "webhook_app", {
        source: "shopify",
        event: "returns/request",
        howItStarts:
          "Fires when a customer submits an exchange request (different size, color, or variant), delivering the order ID, items to exchange, desired replacement variant, and reason.",
        setup:
          "Connect Shopify webhook for return/exchange requests. Filter for exchange type (not refund). Ensure inventory data is accessible.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Shopify: Fetch order and check replacement inventory",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_domain}.myshopify.com/admin/api/2024-01/orders/{order_id}.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            ],
            body: [],
            variables: [
              {
                name: "order.customer.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "order.customer.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "order.name",
                customName: "order_number",
                type: "string",
              },
              {
                name: "order.line_items[0].title",
                customName: "original_item",
                type: "string",
              },
              {
                name: "order.line_items[0].variant_title",
                customName: "original_variant",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Check inventory for requested replacement variant",
        520,
        {
          runCodeDetails: {
            code: "const replacement_available = parseInt(replacement_inventory_qty) > 0; const price_difference = parseFloat(replacement_price) - parseFloat(original_price); const needs_payment = price_difference > 0; const exchange_eligible = replacement_available; return { replacement_available, price_difference: price_difference.toFixed(2), needs_payment, exchange_eligible };",
            variables: [
              {
                name: "replacement_available",
                customName: "replacement_available",
                type: "boolean",
              },
              {
                name: "price_difference",
                customName: "price_difference",
                type: "string",
              },
              {
                name: "needs_payment",
                customName: "needs_payment",
                type: "boolean",
              },
              {
                name: "exchange_eligible",
                customName: "exchange_eligible",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Is the replacement item available?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{replacement_available}",
              operator: "==",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send exchange confirmation to customer",
        1000,
        "{customer_email}",
        "Exchange confirmed for order {order_number}",
        "Hi {first_name},\n\nYour exchange request has been approved!\n\nOriginal Item: {original_item} ({original_variant})\nReplacement: {requested_replacement} ({requested_variant})\n\nPlease return the original item using this prepaid label: {return_label_url}\n\nYour replacement will ship as soon as we receive the return.\n\nThank you,\nSupport Team",
      ),
      n(
        "6",
        "hitApi",
        "Shopify: Create exchange order for replacement item",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{shopify_domain}.myshopify.com/admin/api/2024-01/orders.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "order",
                value:
                  '{"line_items":[{"variant_id":"{replacement_variant_id}","quantity":1}],"customer":{"id":"{customer_id}"},"financial_status":"paid","tags":"exchange,original_order_{order_id}","note":"Exchange for order {order_number}. Original: {original_item} ({original_variant})."}',
              },
            ],
            variables: [
              {
                name: "order.id",
                customName: "exchange_order_id",
                type: "string",
              },
              {
                name: "order.name",
                customName: "exchange_order_number",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Notify fulfillment of exchange order",
        1480,
        ":arrows_counterclockwise: *Exchange Created*\n*Original Order:* {order_number} | *Exchange Order:* {exchange_order_number}\n*Customer:* {first_name}\n*From:* {original_item} ({original_variant})\n*To:* {requested_replacement} ({requested_variant})\n*Reason:* {exchange_reason}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log exchange request and fulfillment",
        1720,
        "Exchange_Requests",
        '[["{order_number}","{exchange_order_number}","{customer_email}","{original_item}","{requested_replacement}","{exchange_reason}","{replacement_available}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes customer exchange requests by fetching the order from Shopify, checking replacement inventory availability, and creating a new exchange order. The customer receives a confirmation email with a return label, the fulfillment team gets a Slack notification, and all exchange details are logged to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 129. Appointment Cancellation Rescheduler | Support | Calendly
  // ────────────────────────────────────────────────────────────────
  appointment_cancellation_rescheduler: {
    nodes: [
      trigger("Calendly webhook: Appointment canceled", "webhook_app", {
        source: "calendly",
        event: "invitee.canceled",
        howItStarts:
          "Fires when a customer cancels a Calendly appointment, delivering the event details, cancellation reason, invitee email, timezone, and original appointment time.",
        setup:
          "Connect Calendly webhook for invitee.canceled events. Ensure scheduling links are configured for rescheduling.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract cancellation reason and suggest reschedule",
        280,
        "You are a scheduling assistant. Analyze the cancellation reason and determine: if the customer wants to reschedule (vs. permanently cancel), the best time to suggest based on their timezone, and an empathetic response. Return wants_reschedule, cancellation_category, timezone, suggested_times, empathetic_message.",
        "Analyze appointment cancellation. Reason: {cancellation_reason}. Original time: {original_appointment_time}. Timezone: {invitee_timezone}. Invitee: {invitee_name}.",
        [
          { field: "wants_reschedule", as: "wants_reschedule", type: "string" },
          {
            field: "cancellation_category",
            as: "cancellation_category",
            type: "string",
          },
          { field: "suggested_times", as: "suggested_times", type: "string" },
          {
            field: "empathetic_message",
            as: "empathetic_message",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Find available reschedule slots", 520, {
        runCodeDetails: {
          code: "const reschedule_link = `https://calendly.com/{calendly_user}/${event_type}?month=${new Date().toISOString().slice(0, 7)}`; const customer_initiated = cancellation_category !== 'provider_canceled'; const follow_up_needed = wants_reschedule === 'true' || wants_reschedule === 'likely'; return { reschedule_link, customer_initiated, follow_up_needed };",
          variables: [
            {
              name: "reschedule_link",
              customName: "reschedule_link",
              type: "string",
            },
            {
              name: "customer_initiated",
              customName: "customer_initiated",
              type: "boolean",
            },
            {
              name: "follow_up_needed",
              customName: "follow_up_needed",
              type: "boolean",
            },
          ],
        },
      }),
      n(
        "4",
        "condition",
        "Check: Was this a customer-initiated cancellation?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{customer_initiated}",
                operator: "==",
                value: "true",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Send reschedule options to customer",
        1000,
        "{invitee_email}",
        "We'd love to reschedule your appointment, {invitee_name}",
        "Hi {invitee_name},\n\n{empathetic_message}\n\nWe'd love to find a time that works better for you. Here are some suggested slots:\n{suggested_times}\n\nOr pick any available time here: {reschedule_link}\n\nIf you'd prefer not to reschedule, no worries at all — we're here whenever you need us.\n\nBest,\nScheduling Team",
      ),
      slackNode(
        "6",
        "Slack: Notify provider of cancellation",
        1240,
        ":calendar: *Appointment Canceled*\n*Customer:* {invitee_name} ({invitee_email})\n*Original Time:* {original_appointment_time}\n*Reason:* {cancellation_category}\n*Wants Reschedule:* {wants_reschedule}\n*Reschedule Link Sent:* {follow_up_needed}",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log cancellation and reschedule attempt",
        1480,
        "Cancellations",
        '[["{invitee_email}","{invitee_name}","{cancellation_category}","{wants_reschedule}","{customer_initiated}","{original_appointment_time}","{today_date}"]]',
      ),
      n("8", "hitApi", "Calendly: Create suggested reschedule event", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            { key: "owner", value: "{calendly_event_uri}" },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "one_time_link",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Handles Calendly appointment cancellations by using AI to analyze the reason and determine if the customer wants to reschedule. Sends an empathetic reschedule email with suggested times and a booking link, notifies the provider via Slack, logs cancellation patterns to Sheets, and creates a one-time Calendly scheduling link.",
  },

  // ────────────────────────────────────────────────────────────────
  // 130. Concierge Chat Follow-Up Summary | Support | Jira
  // ────────────────────────────────────────────────────────────────
  concierge_chat_followup_summary: {
    nodes: [
      trigger("Webhook: VIP concierge chat ended", "webhook_app", {
        source: "intercom",
        event: "conversation.admin.closed",
        howItStarts:
          "Fires when a VIP/concierge chat conversation ends in Intercom, delivering the full transcript, customer details, and conversation metadata.",
        setup:
          "Connect Intercom webhook for conversation.admin.closed events. Filter for conversations tagged 'vip' or 'concierge' to capture only high-touch interactions.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Summarize chat with action items",
        280,
        "You are a customer success analyst. Summarize this VIP concierge chat conversation. Extract: key discussion points, customer requests, commitments made by the agent, action items with owners and deadlines, and overall customer sentiment. Return summary, action_items, commitments, customer_sentiment, follow_up_needed.",
        "Summarize this VIP concierge chat. Transcript: {conversation_transcript}. Customer: {customer_name}. Company: {company_name}.",
        [
          { field: "summary", as: "chat_summary", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "commitments", as: "agent_commitments", type: "string" },
          {
            field: "customer_sentiment",
            as: "customer_sentiment",
            type: "string",
          },
          { field: "follow_up_needed", as: "follow_up_needed", type: "string" },
        ],
      ),
      n("3", "hitApi", "Jira: Create follow-up tasks from action items", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"CS"},"summary":"VIP Follow-Up: {customer_name} — {company_name}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Chat Summary:\\n{chat_summary}\\n\\nAction Items:\\n{action_items}\\n\\nCommitments Made:\\n{agent_commitments}"}]}]},"issuetype":{"name":"Task"},"priority":{"name":"High"}}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_task_key", type: "string" },
          ],
        },
      }),
      emailNode(
        "4",
        "Email: Send conversation summary to VIP customer",
        760,
        "{customer_email}",
        "Summary of our conversation, {customer_name}",
        "Hi {customer_name},\n\nThank you for chatting with us today. Here's a summary of our conversation:\n\n{chat_summary}\n\nAction Items We're Working On:\n{action_items}\n\nCommitments:\n{agent_commitments}\n\nWe'll follow up on each item. If anything looks off, just reply to this email.\n\nBest,\nYour Concierge Team",
      ),
      n("5", "delayAction", "Wait 24 hours to check task completion", 1000, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("6", "runCode", "Check action item completion status", 1240, {
        runCodeDetails: {
          code: "const total_actions = action_items.split('\\n').filter(a => a.trim()).length; const completed = completed_action_count || 0; const completion_rate = total_actions > 0 ? Math.round(completed / total_actions * 100) : 100; const all_fulfilled = completion_rate >= 100; return { total_actions, completed, completion_rate, all_fulfilled };",
          variables: [
            {
              name: "total_actions",
              customName: "total_actions",
              type: "number",
            },
            {
              name: "completion_rate",
              customName: "completion_rate",
              type: "number",
            },
            {
              name: "all_fulfilled",
              customName: "all_fulfilled",
              type: "boolean",
            },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Alert if action items unfulfilled after 24h",
        1480,
        ":rotating_light: *VIP Follow-Up Check — {completion_rate}% Complete*\n*Customer:* {customer_name} ({company_name})\n*Jira Task:* {jira_task_key}\n*Actions:* {completed}/{total_actions} completed\n*Sentiment:* {customer_sentiment}\n\n_Unfulfilled commitments require immediate attention._",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log VIP concierge interaction",
        1720,
        "VIP_Concierge_Log",
        '[["{customer_name}","{company_name}","{customer_sentiment}","{total_actions}","{completion_rate}","{jira_task_key}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a VIP concierge chat ends, AI summarizes the conversation and extracts action items. Creates Jira follow-up tasks, emails the customer a professional summary with commitments made. After 24 hours, checks task completion and alerts Slack if items remain unfulfilled. Logs all VIP interactions to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 131. Escalation Risk Detector | Support | HubSpot
  // ────────────────────────────────────────────────────────────────
  escalation_risk_detector: {
    nodes: [
      trigger("Webhook: Customer interaction received", "webhook_app", {
        source: "intercom",
        event: "conversation.user.replied",
        howItStarts:
          "Fires on each customer reply in an Intercom conversation, delivering the message text, conversation history length, user info, and interaction metadata for frustration analysis.",
        setup:
          "Connect Intercom webhook for conversation.user.replied events. This workflow analyzes each reply for escalation risk signals in real-time.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze frustration signals in customer message",
        280,
        "You are a customer escalation risk analyst. Analyze the customer message for frustration signals: aggressive language, ALL CAPS, threats to churn/cancel, mentions of competitors, repeated issue references, time-sensitivity expressions. Score frustration 0-100. Return frustration_score, signals_detected, escalation_risk, churn_language, recommended_action.",
        "Analyze this customer message for escalation risk signals: {message_text}. Conversation length: {conversation_length} messages. Previous messages: {previous_messages_summary}.",
        [
          {
            field: "frustration_score",
            as: "frustration_score",
            type: "number",
          },
          { field: "signals_detected", as: "signals_detected", type: "string" },
          { field: "escalation_risk", as: "escalation_risk", type: "string" },
          { field: "churn_language", as: "churn_language", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "HubSpot: Fetch customer value and history", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["firstname","company","annual_revenue","plan_tier","total_tickets_last_90d","nps_score"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.company",
              customName: "company_name",
              type: "string",
            },
            {
              name: "results[0].properties.annual_revenue",
              customName: "annual_revenue",
              type: "string",
            },
            {
              name: "results[0].properties.plan_tier",
              customName: "plan_tier",
              type: "string",
            },
            {
              name: "results[0].properties.total_tickets_last_90d",
              customName: "recent_tickets",
              type: "number",
            },
          ],
        },
      }),
      n(
        "4",
        "runCode",
        "Compute escalation score combining frustration and value",
        760,
        {
          runCodeDetails: {
            code: "const revenue = parseFloat(annual_revenue) || 0; const value_multiplier = revenue > 100000 ? 1.5 : revenue > 50000 ? 1.3 : 1.0; const repeat_penalty = recent_tickets > 3 ? 15 : 0; const final_score = Math.min(100, Math.round(frustration_score * value_multiplier + repeat_penalty)); const alert_threshold = final_score > 75; return { escalation_score: final_score, alert_threshold, value_at_risk: revenue, is_repeat_issue: recent_tickets > 3 };",
            variables: [
              {
                name: "escalation_score",
                customName: "escalation_score",
                type: "number",
              },
              {
                name: "alert_threshold",
                customName: "alert_threshold",
                type: "boolean",
              },
              {
                name: "value_at_risk",
                customName: "value_at_risk",
                type: "number",
              },
              {
                name: "is_repeat_issue",
                customName: "is_repeat_issue",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Check: Escalation score > 75?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{escalation_score}", operator: ">", value: "75" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Alert CSM and team lead of escalation risk",
        1240,
        ":rotating_light: *Escalation Risk Alert — Score: {escalation_score}/100*\n*Customer:* {first_name} ({company_name})\n*Plan:* {plan_tier} | *ARR at Risk:* ${value_at_risk}\n*Frustration:* {frustration_score}/100 | *Signals:* {signals_detected}\n*Churn Language:* {churn_language}\n*Repeat Issue:* {is_repeat_issue} ({recent_tickets} tickets in 90d)\n*Recommended:* {recommended_action}",
      ),
      emailNode(
        "7",
        "Email: Send proactive outreach to at-risk customer",
        1480,
        "{customer_email}",
        "{first_name}, I want to personally help resolve this",
        "Hi {first_name},\n\nI noticed you've been experiencing some challenges, and I want to personally make sure we get this resolved for you.\n\nI've escalated your case to our senior team and we're making this a top priority. {recommended_action}\n\nCould we schedule a quick call to discuss? You can book a time here: {calendly_link}\n\nWe truly value {company_name} as a customer and want to make this right.\n\nBest,\n{csm_name}\nSenior Customer Success Manager",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log escalation risk event",
        1720,
        "Escalation_Risks",
        '[["{customer_email}","{company_name}","{escalation_score}","{frustration_score}","{signals_detected}","{value_at_risk}","{is_repeat_issue}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Analyzes each customer reply for frustration signals using AI, combines the frustration score with HubSpot account value data to compute an escalation risk score. If the score exceeds 75, immediately alerts the CSM and lead via Slack, and sends a proactive personal outreach email. All escalation events are logged to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 132. Service Recovery Offer Workflow | Support | HubSpot
  // ────────────────────────────────────────────────────────────────
  service_recovery_offer_workflow: {
    nodes: [
      trigger("Webhook: Negative customer experience reported", "webhook_app", {
        source: "zendesk",
        event: "ticket.tagged",
        howItStarts:
          "Fires when a support ticket is tagged with 'negative_experience' or 'service_failure', delivering the ticket details, customer email, and the nature of the bad experience.",
        setup:
          "Connect Zendesk webhook filtered for tickets tagged with negative experience indicators. Agents tag tickets when a service recovery is warranted.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch customer profile and lifetime value",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["firstname","company","ltv","plan_tier","total_orders","last_recovery_date"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "results[0].properties.company",
                customName: "company_name",
                type: "string",
              },
              {
                name: "results[0].properties.ltv",
                customName: "customer_ltv",
                type: "number",
              },
              {
                name: "results[0].properties.plan_tier",
                customName: "plan_tier",
                type: "string",
              },
              {
                name: "results[0].properties.last_recovery_date",
                customName: "last_recovery_date",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze severity and recommend recovery offer",
        520,
        "You are a service recovery specialist. Analyze the negative experience and recommend an appropriate recovery offer based on severity and customer value. Options: apology_email, discount_10pct, discount_25pct, free_month, account_credit_50, account_credit_100, executive_call. Consider recent recovery history to avoid abuse. Return severity_level, recommended_offer, offer_value, offer_justification, recovery_message.",
        "Analyze negative experience. Issue: {ticket_subject}. Description: {ticket_description}. Customer LTV: ${customer_ltv}. Plan: {plan_tier}. Last recovery: {last_recovery_date}.",
        [
          { field: "severity_level", as: "severity_level", type: "string" },
          {
            field: "recommended_offer",
            as: "recommended_offer",
            type: "string",
          },
          { field: "offer_value", as: "offer_value", type: "number" },
          {
            field: "offer_justification",
            as: "offer_justification",
            type: "string",
          },
          { field: "recovery_message", as: "recovery_message", type: "string" },
        ],
      ),
      n("4", "runCode", "Apply offer limits and budget guardrails", 760, {
        runCodeDetails: {
          code: "const max_offer_pct_of_ltv = 0.05; const max_offer_amount = customer_ltv * max_offer_pct_of_ltv; const within_budget = offer_value <= max_offer_amount; const needs_approval = offer_value > 100; const recent_recovery = last_recovery_date && (Date.now() - new Date(last_recovery_date).getTime()) < 90 * 86400000; const final_offer = recent_recovery ? 'apology_email' : recommended_offer; return { within_budget, needs_approval, recent_recovery, final_offer, max_allowed: max_offer_amount.toFixed(2) };",
          variables: [
            {
              name: "within_budget",
              customName: "within_budget",
              type: "boolean",
            },
            {
              name: "needs_approval",
              customName: "needs_approval",
              type: "boolean",
            },
            {
              name: "recent_recovery",
              customName: "recent_recovery",
              type: "boolean",
            },
            { name: "final_offer", customName: "final_offer", type: "string" },
          ],
        },
      }),
      n(
        "5",
        "condition",
        "Check: Does offer exceed $100 (needs manager approval)?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{needs_approval}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      n(
        "6",
        "approvalGate",
        "Manager: Approve recovery offer over $100",
        1240,
        {
          approvalGateDetails: {
            approvers: ["{support_manager_email}"],
            message:
              "Service Recovery Approval Needed\n\nCustomer: {first_name} ({company_name})\nLTV: ${customer_ltv} | Plan: {plan_tier}\nIssue: {ticket_subject}\nSeverity: {severity_level}\nOffer: {final_offer} (${offer_value})\nJustification: {offer_justification}\nRecent Recovery: {recent_recovery}",
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send recovery offer to customer",
        1480,
        "{customer_email}",
        "We're sorry, {first_name} — here's how we want to make it right",
        "Hi {first_name},\n\n{recovery_message}\n\nAs a gesture of our commitment to your satisfaction, we'd like to offer you: {final_offer}\n\nThis is our way of saying we take your experience seriously and are committed to doing better.\n\nIf there's anything else we can do, please don't hesitate to reach out.\n\nSincerely,\nCustomer Support Leadership",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log service recovery case",
        1720,
        "Service_Recovery",
        '[["{customer_email}","{company_name}","{severity_level}","{final_offer}","{offer_value}","{needs_approval}","{recent_recovery}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Handles negative customer experiences by fetching the customer profile from HubSpot, using AI to recommend an appropriate recovery offer based on severity and LTV, applying budget guardrails. Offers over $100 require manager approval. Sends a personalized recovery email and logs all cases to Sheets for recovery program analysis.",
  },

  // ────────────────────────────────────────────────────────────────
  // 133. Negative Review Service Recovery | Support
  // ────────────────────────────────────────────────────────────────
  negative_review_service_recovery: {
    nodes: [
      trigger("Webhook: Negative review posted (1-2 stars)", "webhook_app", {
        source: "zendesk",
        event: "review.created",
        howItStarts:
          "Fires when a customer posts a 1 or 2-star review on any monitored platform (G2, Trustpilot, Google), delivering the review text, rating, reviewer name, and platform.",
        setup:
          "Connect review monitoring webhook (via Zendesk, or direct G2/Trustpilot API). Filter for ratings of 1-2 stars requiring immediate attention.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract review content and identify customer",
        280,
        "You are a review response specialist. Analyze the negative review to extract: key complaints, product/service areas mentioned, emotional tone, and any identifying info (company name, email, order number). Draft both a professional public response and a private outreach message. Return complaints, product_areas, emotional_tone, reviewer_identifier, public_response, private_message.",
        "Analyze this negative review. Rating: {review_rating}/5. Platform: {review_platform}. Review: {review_text}. Reviewer: {reviewer_name}.",
        [
          { field: "complaints", as: "key_complaints", type: "string" },
          { field: "product_areas", as: "product_areas", type: "string" },
          { field: "emotional_tone", as: "emotional_tone", type: "string" },
          {
            field: "reviewer_identifier",
            as: "reviewer_identifier",
            type: "string",
          },
          { field: "public_response", as: "public_response", type: "string" },
          { field: "private_message", as: "private_message", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Lookup reviewer in CRM", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"CONTAINS_TOKEN","value":"{reviewer_identifier}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.company",
              customName: "company_name",
              type: "string",
            },
            {
              name: "results[0].properties.plan_tier",
              customName: "plan_tier",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "OpenAI: Draft personalized public and private responses",
        760,
        "You are a reputation management expert. Using the customer profile, refine the public response to be professional and empathetic without being defensive. Craft a private remediation message that addresses specific complaints. Return final_public_response, final_private_response, remediation_offer.",
        "Refine responses for {first_name} at {company_name}. Rating: {review_rating}. Complaints: {key_complaints}. Public draft: {public_response}. Private draft: {private_message}. Plan: {plan_tier}.",
        [
          {
            field: "final_public_response",
            as: "final_public_response",
            type: "string",
          },
          {
            field: "final_private_response",
            as: "final_private_response",
            type: "string",
          },
          {
            field: "remediation_offer",
            as: "remediation_offer",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "Check: Is the rating 2 or lower?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{review_rating}", operator: "<=", value: "2" },
          ],
        },
      }),
      n(
        "6",
        "approvalGate",
        "Manager: Approve public and private responses",
        1240,
        {
          approvalGateDetails: {
            approvers: ["{support_manager_email}"],
            message:
              "Negative Review Response Approval\n\nPlatform: {review_platform} | Rating: {review_rating}/5\nCustomer: {first_name} at {company_name}\nComplaints: {key_complaints}\n\nPublic Response:\n{final_public_response}\n\nPrivate Message:\n{final_private_response}\n\nRemediation: {remediation_offer}",
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send private remediation outreach",
        1480,
        "{customer_email}",
        "{first_name}, we take your feedback seriously",
        "Hi {first_name},\n\n{final_private_response}\n\nRemediation: {remediation_offer}\n\nWe'd love the opportunity to make this right. Would you be open to a brief call? Reply to this email or book a time here: {calendly_link}\n\nSincerely,\n{csm_name}\nHead of Customer Success",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log negative review and response",
        1720,
        "Negative_Reviews",
        '[["{review_platform}","{review_rating}","{reviewer_name}","{company_name}","{key_complaints}","{emotional_tone}","{remediation_offer}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Handles negative 1-2 star reviews by extracting complaints with AI, identifying the reviewer in HubSpot, and drafting both a public response and private remediation message. For the lowest-rated reviews, requires manager approval before sending the private outreach email with a remediation offer. Logs all reviews and responses to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 134. Help-Center 'Not Helpful' Follow-Up | Support
  // ────────────────────────────────────────────────────────────────
  help_center_not_helpful_followup: {
    nodes: [
      trigger("Webhook: Article rated 'Not Helpful'", "webhook_app", {
        source: "zendesk",
        event: "article.vote_down",
        howItStarts:
          "Fires when a customer clicks 'Not Helpful' on a help center article, delivering the article ID, title, URL, voter email (if logged in), and current helpfulness stats.",
        setup:
          "Connect your help center widget to send a webhook when a negative vote (thumbs down) is registered on any article.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze why article may not have helped",
        280,
        "You are a knowledge base analyst. Analyze the article content against common reasons it might be unhelpful: outdated information, missing steps, too technical, doesn't address the actual question, unclear instructions. Suggest what the customer likely needed. Return likely_gap, gap_category, search_query_guess, alternative_suggestion, article_improvement.",
        "Analyze why this article was rated unhelpful. Article title: {article_title}. Article URL: {article_url}. Article content summary: {article_excerpt}. Voter context: {voter_page_referrer}.",
        [
          { field: "likely_gap", as: "likely_gap", type: "string" },
          { field: "gap_category", as: "gap_category", type: "string" },
          {
            field: "search_query_guess",
            as: "search_query_guess",
            type: "string",
          },
          {
            field: "alternative_suggestion",
            as: "alternative_suggestion",
            type: "string",
          },
          {
            field: "article_improvement",
            as: "article_improvement",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Identify content gap and match to existing articles",
        520,
        {
          runCodeDetails: {
            code: "const gap_identified = likely_gap && likely_gap !== 'none'; const needs_new_article = gap_category === 'missing_topic'; const negative_vote_count = (article_total_down_votes || 0) + 1; const unhelpful_rate = negative_vote_count / Math.max(article_total_votes, 1) * 100; const high_priority = unhelpful_rate > 30; return { gap_identified, needs_new_article, negative_vote_count, unhelpful_rate: Math.round(unhelpful_rate), high_priority };",
            variables: [
              {
                name: "gap_identified",
                customName: "gap_identified",
                type: "boolean",
              },
              {
                name: "needs_new_article",
                customName: "needs_new_article",
                type: "boolean",
              },
              {
                name: "unhelpful_rate",
                customName: "unhelpful_rate",
                type: "number",
              },
              {
                name: "high_priority",
                customName: "high_priority",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Was a content gap identified?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{gap_identified}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Offer alternative help to the customer",
        1000,
        "{voter_email}",
        "Sorry that article wasn't helpful — let us assist you",
        "Hi there,\n\nWe noticed the article '{article_title}' wasn't helpful for what you needed. We're sorry about that.\n\n{alternative_suggestion}\n\nIf you're still looking for help, you can:\n- Search our help center: {help_center_url}\n- Contact our support team directly: {support_email}\n- Start a live chat for immediate assistance\n\nWe're always working to improve our resources.\n\nBest,\nSupport Team",
      ),
      slackNode(
        "6",
        "Slack: Alert content team about content gap",
        1240,
        ":mag: *Content Gap Detected — 'Not Helpful' Vote*\n*Article:* {article_title}\n*Unhelpful Rate:* {unhelpful_rate}% ({negative_vote_count} down votes)\n*Gap Category:* {gap_category}\n*Likely Gap:* {likely_gap}\n*Improvement:* {article_improvement}\n*Priority:* {high_priority}",
      ),
      n(
        "7",
        "hitApi",
        "Jira: Create content update task if high priority",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"CONTENT"},"summary":"Update KB Article: {article_title} (unhelpful rate: {unhelpful_rate}%)","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Gap: {likely_gap}\\nCategory: {gap_category}\\nImprovement: {article_improvement}\\nUnhelpful Rate: {unhelpful_rate}%"}]}]},"issuetype":{"name":"Task"},"priority":{"name":"High"}}',
              },
            ],
            variables: [
              { name: "key", customName: "jira_task_key", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log unhelpful article vote",
        1720,
        "KB_Feedback",
        '[["{article_title}","{article_url}","{gap_category}","{likely_gap}","{unhelpful_rate}","{high_priority}","{jira_task_key}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a help center article gets a 'Not Helpful' vote, AI analyzes the likely content gap, emails the customer alternative help suggestions, alerts the content team via Slack, and creates a Jira task for high-priority article updates. Tracks unhelpful rates and content gaps in Sheets for systematic KB improvement.",
  },

  // ────────────────────────────────────────────────────────────────
  // 135. Customer Preference Center Sync | Support | HubSpot
  // ────────────────────────────────────────────────────────────────
  customer_preference_center_sync: {
    nodes: [
      trigger(
        "Webhook: Customer updated communication preferences",
        "webhook_app",
        {
          source: "hubspot",
          event: "contact.propertyChange",
          howItStarts:
            "Fires when a customer updates their communication preferences (email opt-in/out, SMS consent, channel preferences, frequency settings) via the preference center.",
          setup:
            "Connect your preference center form to trigger a webhook on submission. Ensure all preference fields are mapped to HubSpot contact properties.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "runCode",
        "Validate preferences for compliance requirements",
        280,
        {
          runCodeDetails: {
            code: "const email_opted_out = preferences_email === 'false' || preferences_email === 'opt_out'; const sms_opted_out = preferences_sms === 'false' || preferences_sms === 'opt_out'; const critical_opt_out = email_opted_out && sms_opted_out; const changes = []; if (preferences_email_changed) changes.push('email: ' + preferences_email); if (preferences_sms_changed) changes.push('sms: ' + preferences_sms); if (preferences_frequency_changed) changes.push('frequency: ' + preferences_frequency); const gdpr_compliant = true; return { email_opted_out, sms_opted_out, critical_opt_out, changes_summary: changes.join(', '), gdpr_compliant };",
            variables: [
              {
                name: "email_opted_out",
                customName: "email_opted_out",
                type: "boolean",
              },
              {
                name: "sms_opted_out",
                customName: "sms_opted_out",
                type: "boolean",
              },
              {
                name: "critical_opt_out",
                customName: "critical_opt_out",
                type: "boolean",
              },
              {
                name: "changes_summary",
                customName: "changes_summary",
                type: "string",
              },
              {
                name: "gdpr_compliant",
                customName: "gdpr_compliant",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Update all communication preferences on contact",
        520,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email_opt_in":"{preferences_email}","sms_opt_in":"{preferences_sms}","preferred_channel":"{preferences_channel}","email_frequency":"{preferences_frequency}","preferences_updated_at":"{timestamp}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Is this a critical opt-out (all channels)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{critical_opt_out}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Confirm preference changes to customer",
        1000,
        "{customer_email}",
        "Your communication preferences have been updated",
        "Hi {first_name},\n\nWe've updated your communication preferences as requested:\n\n{changes_summary}\n\nThese changes take effect immediately across all our channels.\n\nIf you didn't make these changes or want to update them again, visit your preference center: {preference_center_url}\n\nThank you,\nOmniEngage Team",
      ),
      slackNode(
        "6",
        "Slack: Alert compliance team of critical opt-out",
        1240,
        ":rotating_light: *Critical Opt-Out — All Channels*\n*Customer:* {first_name} ({customer_email})\n*Company:* {company_name}\n*Changes:* {changes_summary}\n*All Channels Opted Out:* {critical_opt_out}\n\n_Please verify this is intentional and ensure all downstream systems are updated._",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Audit log preference changes",
        1480,
        "Preference_Audit",
        '[["{customer_email}","{first_name}","{changes_summary}","{email_opted_out}","{sms_opted_out}","{critical_opt_out}","{gdpr_compliant}","{today_date}"]]',
      ),
      n("8", "runCode", "Propagate preferences to downstream systems", 1720, {
        runCodeDetails: {
          code: "const systems_to_update = ['email_platform', 'sms_platform', 'push_notification', 'ad_audiences']; const suppression_needed = email_opted_out || sms_opted_out; const propagation_list = systems_to_update.filter(s => { if (s === 'email_platform') return true; if (s === 'sms_platform') return true; return suppression_needed; }); return { systems_updated: propagation_list.join(', '), suppression_needed, propagation_count: propagation_list.length };",
          variables: [
            {
              name: "systems_updated",
              customName: "systems_updated",
              type: "string",
            },
            {
              name: "suppression_needed",
              customName: "suppression_needed",
              type: "boolean",
            },
            {
              name: "propagation_count",
              customName: "propagation_count",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes customer preference center updates by validating compliance requirements, updating all preferences in HubSpot, confirming changes via email, and alerting the compliance team for critical all-channel opt-outs. Maintains a GDPR-compliant audit log in Sheets and propagates preference changes to all downstream systems.",
  },
};
